import { observer } from "mobx-react";
import * as React from "react";
import { TableChildProps } from "../coreui/Table";
import { TableContext } from "../coreui/table/TableContext";
import Typography from "../coreui/Typography";
import {
  RuntimeProperties,
  RuntimeWidgetT,
  WidgetValue,
} from "../models/PaneRow";
import { CheckBoxColumn } from "./Columns/CheckBoxColumn";
import { ComponentTypeDisplayColumn } from "./Columns/ComponentTypeDisplayColumn";
import { DataImageDisplayColumn } from "./Columns/DataImageDisplayColumn";
import { DataLinkColumn } from "./Columns/DataLinkColumn";
import { DateEditColumn } from "./Columns/DateEditColumn";
import { DomainComboBoxColumnDisplay } from "./Columns/DomainComboBoxColumnDisplay";
import { GridColumnConfigProperties } from "./Columns/GridColumn";
import { IconDisplayColumn } from "./Columns/IconDisplayColumn";
import { ManualLinkColumn } from "./Columns/ManualLinkColumn";
import * as MarkdownDialogColumn from "./Columns/MarkdownDialogColumn";
import { NumericEditColumn } from "./Columns/NumericEditColumn";
import { RelationshipComboBoxColumnDisplay } from "./Columns/RelationshipComboBoxColumnDisplay";
import { TextColumn } from "./Columns/TextColumn";

interface ConfigProperties {
  columnWidgetName: string;
  dataId: string;
  functionName: FunctionName;
  label: string;
  name: string;
  propagated: TableChildProps;
}

export type FunctionName = "Average" | "Count" | "Maximum" | "Minimum" | "Sum";

const TableSummary = observer((props: ConfigProperties): JSX.Element => {
  const { visibleRows } = React.useContext(TableContext);

  let columnConfigProperties: GridColumnConfigProperties | undefined;
  for (const column of props.propagated.parentTable.columns) {
    const gridColumn = column as GridColumnConfigProperties;
    if (gridColumn.name === props.columnWidgetName) {
      columnConfigProperties = gridColumn;
      break;
    }
  }

  if (!columnConfigProperties) {
    throw new Error(`Column ${props.columnWidgetName} not found`);
  }

  const runtimeData: RuntimeWidgetT[] = [];
  for (const row of visibleRows) {
    runtimeData.push(
      row.getWidgetT<WidgetValue, RuntimeProperties>(props.columnWidgetName)
    );
  }

  let getSummaryValue: (
    runtimeData: RuntimeWidgetT[],
    configProperties: object,
    functionName: FunctionName
  ) => string | null;

  const widgetType: string = columnConfigProperties.widgetType;
  switch (widgetType) {
    case "CheckBoxColumn":
      getSummaryValue = CheckBoxColumn.getSummaryValue;
      break;
    case "ComponentTypeDisplayColumn":
      getSummaryValue = ComponentTypeDisplayColumn.getSummaryValue;
      break;
    case "DataImageDisplayColumn":
      getSummaryValue = DataImageDisplayColumn.getSummaryValue;
      break;
    case "DataLinkColumn":
      getSummaryValue = DataLinkColumn.getSummaryValue;
      break;
    case "DateEditColumn":
      getSummaryValue = DateEditColumn.getSummaryValue;
      break;
    case "DomainComboBoxColumn":
      getSummaryValue = DomainComboBoxColumnDisplay.getSummaryValue;
      break;
    case "IconDisplayColumn":
      getSummaryValue = IconDisplayColumn.getSummaryValue;
      break;
    case "ManualLinkColumn":
      getSummaryValue = ManualLinkColumn.getSummaryValue;
      break;
    case "MarkdownDialogColumn":
      getSummaryValue = MarkdownDialogColumn.getSummaryValue;
      break;
    case "MLTextEditColumn":
      getSummaryValue = TextColumn.getSummaryValue;
      break;
    case "NumericEditColumn":
      getSummaryValue = NumericEditColumn.getSummaryValue;
      break;
    case "RelationshipComboBoxColumn":
      getSummaryValue = RelationshipComboBoxColumnDisplay.getSummaryValue;
      break;
    case "SLTextEditColumn":
      getSummaryValue = TextColumn.getSummaryValue;
      break;
    case "TextDisplayColumn":
      getSummaryValue = TextColumn.getSummaryValue;
      break;
    default:
      throw new Error(`Unknown summary widget type ${widgetType}`);
  }

  const value: string | null = getSummaryValue(
    runtimeData,
    columnConfigProperties.widgetProps,
    props.functionName
  );

  const summaryText = `${props.label}: ${value === null ? "" : value}`;
  return <Typography style={{ fontSize: 14 }}>{summaryText}</Typography>;
});

export default TableSummary;
